import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getAfterleaseVehicleCalculator,
  getCalculatorsDataByAttractivenessLevel,
  getNewVehicleCalculator,
  getPromoCodeCalculator,
  getPromotionForAuction
} from "../../../../services";
import {
  getPromoCodeFromSearch,
  parsePromoCodeParam,
  replacePromoCodeInCurrentUrl,
  saveAppliedPromoCode
} from "../../../../scripts/commons/promoCode";
import type { CalculatorData, LeasingFormOptions, RentFormOptions } from "../types/calculatorData";
import {
  buildAdditionalServiceItemsFromAdditions,
  type AdditionalServiceItem,
  type PackageState,
  useAdditionalPackages
} from "../utils/additionalPackagesService";
import { applyCalculatorRadioChange, CalculatorRadioChangeEvent } from "../utils/applyCalculatorRadioChange";
import { appendBrokerUrlParams } from "../utils/appendBrokerUrlParams";
import { buildCalcOptionsForContactUrl } from "../utils/buildCalcOptionsForContactUrl";
import {
  buildPrintOfferPayload,
  type CalculatorContactFormDataPayload,
  type PrintOfferPayload
} from "../utils/buildPrintOfferPayload";
import { createURLToContactSection } from "../utils/createURLToContactSection";
import {
  getSelectedPricesFromCalculatorData,
  type SelectedCalculatorPrices
} from "../utils/getSelectedCalculatorPrice";
import {
  mapCalculatorDataToFormOptions,
  mapLeasingPeriodsToFormOptions,
  mapRentPeriodsToFormOptions
} from "../utils/mapCalculatorFormOptions";
import { getPackageValueForKind, PackageKind } from "../utils/packageEnumUtils";

export interface CalculatorSnapshot {
  calculatorData: CalculatorData;
  selectedPrices: SelectedCalculatorPrices | null;
  activePackageState: PackageState;
  activeTab: number;
  installmentPrice: number | undefined;
  installmentPriceWithoutPackages: number | null;
  urlObject: string;
  contactFormPayload: CalculatorContactFormDataPayload | null;
}

export interface UseCalculatorDataParams {
  carId: number;
  pageType: string;
  isRentAvailable: boolean;
  isLeasingAvailable: boolean;
  isCashAvailable: boolean;
  isElectric: boolean;
  literals?: Record<string, string | undefined> | null;
  attractivenessLevel?: string | null;
  templateId?: string | null;
  vendorType?: string | null;
  login?: string | null;
  onActiveTabChange?: (tab: number) => void;
  onContactUrl?: (url: string) => void;
  onCalculatorSnapshot?: (snapshot: CalculatorSnapshot) => void;
  totalPriceBrutto: number;
  /** false for broker links and reserved cars - promo code is neither applied nor sent with contact forms */
  isPromoCodeEnabled?: boolean;
}

function buildSelectedServicesParam(packageState: PackageState): string {
  return Object.values(packageState)
    .filter((entry) => entry.isChecked)
    .map((entry) => entry.Value)
    .sort((a, b) => a - b)
    .join(",");
}

function filterInfoPackageItems(
  items: AdditionalServiceItem[],
  pageType: string,
  activeTab: number
): AdditionalServiceItem[] {
  if (pageType === "new" && activeTab === 1) {
    const prestigeNonAsoValue = getPackageValueForKind(PackageKind.ManagementPrestigeNonAso);
    const prestigeAsoValue = getPackageValueForKind(PackageKind.ManagementPrestige);
    const hasTwoPrestigePackage = [prestigeNonAsoValue, prestigeAsoValue].every((val) =>
      items.some((obj) => obj.Value === val)
    );
    const filtered = items.filter((pkg) => pkg.Value !== prestigeNonAsoValue);
    return hasTwoPrestigePackage ? filtered : items;
  }
  return items;
}

function getInitialTab(
  isCompany: boolean,
  isCashAvailable: boolean,
  isLeasingAvailable: boolean,
  isRentAvailable: boolean
): number {
  if (!isCompany && isCashAvailable) {
    return 3;
  }

  const rememberedTab = sessionStorage.getItem("mem_calc_tab");
  const compareLeaseToRent = sessionStorage.getItem("lowestInstallment");

  if (rememberedTab) {
    return Number(rememberedTab);
  }

  if (isLeasingAvailable && compareLeaseToRent === "lease") {
    return 2;
  }
  if (isRentAvailable) {
    return 1;
  }
  if (isLeasingAvailable) {
    return 2;
  }
  return 3;
}

const EMPTY_CALCULATOR_DATA: CalculatorData = {
  RentPeriods: [],
  RentAdditions: {
    Configuration: [],
    Rent_ManagementBasic: [],
    Rent_ManagementComfort: [],
    Rent_ManagementPrestige: [],
    Rent_ManagementPrestigeAso: [],
    Tires: [],
    Assistance: 0,
    PetrolCard: 0
  },
  LeasingPeriods: [],
  LeasingAdditions: { Configuration: [], Assistance: 0, TechnicalService: 0, Tires: 0, PetrolCard: 0 },
  Insurance: [],
  InsuranceAdditionalText: null
};

async function fetchCalculatorData(carId: number, pageType: string): Promise<CalculatorData | null> {
  const response =
    pageType === "new"
      ? await getNewVehicleCalculator(carId)
      : await getAfterleaseVehicleCalculator(carId);

  if (response.status >= 200 && response.status <= 299 && response.data) {
    return response.data as CalculatorData;
  }
  return null;
}

const PROMO_CODE_MESSAGES = {
  success: "kod promocyjny został zastosowany",
  notApplicable: "kod promocyjny nie dotyczy tego samochodu",
  notApplicableForTab: "kod promocyjny nie dotyczy tej formy finansowania",
  error: "nie udało się zastosować kodu promocyjnego, spróbuj ponownie"
};

const RENT_TAB = 1;
const LEASING_TAB = 2;

type PromoCodeStatus = "idle" | "loading" | "success" | "error";

interface PromoCodeState {
  status: PromoCodeStatus;
  message: string;
  appliedCode: string;
}

interface ActivePromo {
  code: string;
  referer: string;
  calcRent: boolean;
  calcLeasing: boolean;
}

const isPromoTab = (promo: ActivePromo | null, tab: number): boolean =>
  Boolean(promo) && ((tab === RENT_TAB && promo!.calcRent) || (tab === LEASING_TAB && promo!.calcLeasing));

const isSuccessResponse = (response: any): boolean => response?.status >= 200 && response?.status <= 299;

const getResponseErrorMessage = (response: any): string | null => {
  const data = response?.data;
  if (!data) return null;
  if (typeof data === "string") return data;
  return data.Message || data.message || null;
};

/** Checks which calculators (rent / leasing) promo code applies to for given auction. */
async function fetchPromotionForAuction(
  carId: number,
  pageType: string,
  promoCode: string
): Promise<{ promo: Omit<ActivePromo, "referer"> | null; isNotApplicable: boolean }> {
  const response = await getPromotionForAuction(carId, promoCode, pageType !== "new");
  if (isSuccessResponse(response) && response.data) {
    const calcRent = Boolean(response.data.CalcRent);
    const calcLeasing = Boolean(response.data.CalcLeasing);
    if (!calcRent && !calcLeasing) {
      return { promo: null, isNotApplicable: true };
    }
    return { promo: { code: response.data.Code || promoCode, calcRent, calcLeasing }, isNotApplicable: false };
  }
  return { promo: null, isNotApplicable: response?.status === 404 };
}

async function fetchPromoCalculatorForTab(
  calculatorData: CalculatorData,
  carId: number,
  pageType: string,
  promo: ActivePromo,
  tab: number
): Promise<{ data: CalculatorData | null; errorMessage: string | null }> {
  const isRent = tab === RENT_TAB;
  const response = await getPromoCodeCalculator(carId, promo.code, promo.referer, pageType, isRent);
  if (!isSuccessResponse(response) || !response.data) {
    return { data: null, errorMessage: getResponseErrorMessage(response) };
  }

  const merged: CalculatorData = { ...calculatorData };
  if (isRent) {
    if (!response.data.RentPeriods?.length) return { data: null, errorMessage: null };
    merged.RentPeriods = response.data.RentPeriods;
    merged.RentAdditions = response.data.RentAdditions ?? calculatorData.RentAdditions;
  } else {
    if (!response.data.LeasingPeriods?.length) return { data: null, errorMessage: null };
    merged.LeasingPeriods = response.data.LeasingPeriods;
    merged.LeasingAdditions = response.data.LeasingAdditions ?? calculatorData.LeasingAdditions;
  }
  return { data: merged, errorMessage: null };
}

export function useCalculatorData(params: UseCalculatorDataParams) {
  const {
    carId,
    pageType,
    isRentAvailable,
    isLeasingAvailable,
    isCashAvailable,
    isElectric,
    literals,
    attractivenessLevel,
    templateId,
    vendorType,
    login,
    onActiveTabChange,
    onContactUrl,
    onCalculatorSnapshot,
    totalPriceBrutto,
    isPromoCodeEnabled = true
  } = params;

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [calcOptionsRaw, setCalcOptionsRaw] = useState<CalculatorData & { AuctionId: string } | null>(null);
  const [rentCalculatorData, setRentCalculatorData] = useState<RentFormOptions | null>(null);
  const [leasingCalculatorData, setLeasingCalculatorData] = useState<LeasingFormOptions | null>(null);
  const [installmentPrice, setInstallmentPrice] = useState<number | undefined>(undefined);
  const [installmentPriceWithoutPackages, setInstallmentPriceWithoutPackages] = useState<number | null>(null);
  const [urlObject, setUrlObject] = useState("");
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoCodeState, setPromoCodeState] = useState<PromoCodeState>({
    status: "idle",
    message: "",
    appliedCode: ""
  });

  const [activePromo, setActivePromo] = useState<ActivePromo | null>(null);
  const [urlPromoCode, setUrlPromoCode] = useState(() => getPromoCodeFromSearch().promocode);

  const promoRequestIdRef = useRef(0);
  const promoTabRequestIdRef = useRef(0);
  const isPromoCodeActiveRef = useRef(false);
  const activePromoRef = useRef<ActivePromo | null>(null);
  const promoAppliedTabsRef = useRef<number[]>([]);
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const calcOptionsRawRef = useRef(calcOptionsRaw);
  calcOptionsRawRef.current = calcOptionsRaw;

  const onActiveTabChangeRef = useRef(onActiveTabChange);
  const onContactUrlRef = useRef(onContactUrl);
  const onCalculatorSnapshotRef = useRef(onCalculatorSnapshot);
  const lastSnapshotKeyRef = useRef("");

  onActiveTabChangeRef.current = onActiveTabChange;
  onContactUrlRef.current = onContactUrl;
  onCalculatorSnapshotRef.current = onCalculatorSnapshot;

  const applyCalculatorState = useCallback((data: CalculatorData) => {
    const withAuctionId = { ...data, AuctionId: String(carId) };
    const formOptions = mapCalculatorDataToFormOptions(data);
    setCalcOptionsRaw(withAuctionId);
    setRentCalculatorData(formOptions.rent);
    setLeasingCalculatorData(formOptions.leasing);
    setIsError(false);
  }, [carId]);

  const loadBaseCalculator = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchCalculatorData(carId, pageType);
      if (data) {
        applyCalculatorState(data);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [carId, pageType, applyCalculatorState]);

  const persistActiveTab = useCallback((tab: number) => {
    setActiveTab((prev) => {
      if (prev === tab) {
        return prev;
      }
      sessionStorage.setItem("mem_calc_tab", String(tab));
      onActiveTabChangeRef.current?.(tab);
      return tab;
    });
  }, []);

  const resetPromoCode = useCallback(() => {
    promoTabRequestIdRef.current++;
    isPromoCodeActiveRef.current = false;
    activePromoRef.current = null;
    promoAppliedTabsRef.current = [];
    setActivePromo(null);
    saveAppliedPromoCode(carId, null);
  }, [carId]);

  const activatePromoCode = useCallback(
    async (rawCode: string, promoReferer: string, isInitialLoad: boolean) => {
      const promoCode = rawCode.trim();
      if (!promoCode) {
        return;
      }

      const requestId = ++promoRequestIdRef.current;
      promoTabRequestIdRef.current++;
      const isCurrentRequest = () => requestId === promoRequestIdRef.current;
      const hadActivePromo = Boolean(activePromoRef.current);

      setPromoCodeState({ status: "loading", message: "", appliedCode: "" });
      setIsLoading(true);

      const failPromoCode = (baseData: CalculatorData | null, message: string) => {
        // on initial load or when other promo code was applied - show calculator without promo
        if (baseData && (isInitialLoad || hadActivePromo)) {
          applyCalculatorState(baseData);
        }
        resetPromoCode();
        setPromoCodeState({ status: "error", message, appliedCode: "" });
      };

      try {
        const [baseData, promotion] = await Promise.all([
          fetchCalculatorData(carId, pageType),
          fetchPromotionForAuction(carId, pageType, promoCode).catch(() => ({ promo: null, isNotApplicable: false }))
        ]);
        if (!isCurrentRequest()) return;

        if (!baseData) {
          if (isInitialLoad) setIsError(true);
          failPromoCode(null, PROMO_CODE_MESSAGES.error);
          return;
        }

        if (!promotion.promo) {
          failPromoCode(
            baseData,
            promotion.isNotApplicable ? PROMO_CODE_MESSAGES.notApplicable : PROMO_CODE_MESSAGES.error
          );
          return;
        }

        const promo: ActivePromo = { ...promotion.promo, referer: promoReferer };
        const currentTab = activeTabRef.current;
        const targetTab = isPromoTab(promo, currentTab) ? currentTab : promo.calcRent ? RENT_TAB : LEASING_TAB;

        const result = await fetchPromoCalculatorForTab(baseData, carId, pageType, promo, targetTab);
        if (!isCurrentRequest()) return;

        if (!result.data) {
          failPromoCode(baseData, result.errorMessage || PROMO_CODE_MESSAGES.notApplicable);
          return;
        }

        isPromoCodeActiveRef.current = true;
        activePromoRef.current = promo;
        promoAppliedTabsRef.current = [targetTab];
        setActivePromo(promo);
        applyCalculatorState(result.data);
        persistActiveTab(targetTab);
        setPromoCodeInput(promo.code);
        saveAppliedPromoCode(carId, { promocode: promo.code, promoreferer: promo.referer });
        // applied promo code is kept in details page url (?promocode=CODE&promorefferer=REFERER)
        replacePromoCodeInCurrentUrl({ promocode: promo.code, promoreferer: promo.referer });
        setPromoCodeState({ status: "success", message: PROMO_CODE_MESSAGES.success, appliedCode: promo.code });
      } catch {
        if (!isCurrentRequest()) return;
        if (isInitialLoad) setIsError(true);
        failPromoCode(null, PROMO_CODE_MESSAGES.error);
      } finally {
        if (isCurrentRequest()) {
          setIsLoading(false);
        }
      }
    },
    [carId, pageType, applyCalculatorState, persistActiveTab, resetPromoCode]
  );

  useEffect(() => {
    const handlePopState = () => setUrlPromoCode(getPromoCodeFromSearch().promocode);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const initialPromo = isPromoCodeEnabled ? getPromoCodeFromSearch() : { promocode: "", promoreferer: "" };
    if (initialPromo.promocode) {
      isPromoCodeActiveRef.current = true;
      setPromoCodeInput(initialPromo.promocode);
      activatePromoCode(initialPromo.promocode, initialPromo.promoreferer, true);
    } else {
      promoRequestIdRef.current++;
      resetPromoCode();
      setPromoCodeInput("");
      setPromoCodeState({ status: "idle", message: "", appliedCode: "" });
      loadBaseCalculator();
    }
  }, [carId, isPromoCodeEnabled, urlPromoCode, loadBaseCalculator, activatePromoCode, resetPromoCode]);

  useEffect(() => {
    const promo = activePromoRef.current;
    const currentData = calcOptionsRawRef.current;
    if (!promo || !currentData || !isPromoTab(promo, activeTab) || promoAppliedTabsRef.current.includes(activeTab)) {
      return;
    }

    const requestId = ++promoTabRequestIdRef.current;
    const isCurrentRequest = () => requestId === promoTabRequestIdRef.current;
    setIsLoading(true);

    fetchPromoCalculatorForTab(currentData, carId, pageType, promo, activeTab)
      .then((result) => {
        if (!isCurrentRequest()) return;
        if (result.data) {
          promoAppliedTabsRef.current = [...promoAppliedTabsRef.current, activeTab];
          applyCalculatorState(result.data);
          setPromoCodeState({ status: "success", message: PROMO_CODE_MESSAGES.success, appliedCode: promo.code });
        } else {
          setPromoCodeState({
            status: "error",
            message: result.errorMessage || PROMO_CODE_MESSAGES.error,
            appliedCode: promo.code
          });
        }
      })
      .catch(() => {
        if (!isCurrentRequest()) return;
        setPromoCodeState({ status: "error", message: PROMO_CODE_MESSAGES.error, appliedCode: promo.code });
      })
      .finally(() => {
        if (isCurrentRequest()) {
          setIsLoading(false);
        }
      });
  }, [activeTab, activePromo, carId, pageType, applyCalculatorState]);

  const applyPromoCode = useCallback(() => {
    const parsed = parsePromoCodeParam(promoCodeInput);
    const promoCode = parsed.promocode;
    if (!promoCode || !isPromoCodeEnabled) return;
    const fromUrl = getPromoCodeFromSearch();
    const promoReferer =
      parsed.promoreferer ||
      (fromUrl.promocode.toLowerCase() === promoCode.toLowerCase() ? fromUrl.promoreferer : "");
    setPromoCodeInput(promoCode);
    activatePromoCode(promoCode, promoReferer, false);
  }, [promoCodeInput, isPromoCodeEnabled, activatePromoCode]);

  const isPromoCodeForInput =
    Boolean(activePromo) && promoCodeInput.trim().toLowerCase() === activePromo!.code.toLowerCase();
  const isPromoCodeForActiveTab = isPromoTab(activePromo, activeTab);
  const isPromoCodeApplied =
    promoCodeState.status === "success" && isPromoCodeForInput && isPromoCodeForActiveTab;

  const calculatorDataForPackages = calcOptionsRaw ?? EMPTY_CALCULATOR_DATA;

  const {
    activePackageState,
    packageTiles,
    installmentWithPackages
  } = useAdditionalPackages({
    calculatorData: calculatorDataForPackages,
    pageType,
    activeTab,
    isRentAvailable,
    isElectric,
    literals
  });

  const selectedServices = useMemo(
    () => buildSelectedServicesParam(activePackageState),
    [activePackageState]
  );

  useEffect(() => {
    if (!templateId || !attractivenessLevel) {
      return;
    }

    if (attractivenessLevel === "0") {
      if (!isPromoCodeActiveRef.current) {
        loadBaseCalculator();
      }
      return;
    }

    let cancelled = false;

    getCalculatorsDataByAttractivenessLevel(
      carId,
      templateId,
      attractivenessLevel,
      vendorType,
      login,
      selectedServices
    )
      .then(async (response) => {
        if (cancelled || !response?.data) {
          return;
        }

        const baseData = await fetchCalculatorData(carId, pageType);
        if (!baseData) {
          return;
        }

        const merged = { ...baseData };
        Object.keys(response.data).forEach((key) => {
          if (response.data.hasOwnProperty(key)) {
            (merged as Record<string, unknown>)[key] = response.data[key];
          }
        });
        applyCalculatorState(merged);
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      cancelled = true;
    };
  }, [
    attractivenessLevel,
    templateId,
    vendorType,
    login,
    carId,
    pageType,
    selectedServices,
    loadBaseCalculator,
    applyCalculatorState
  ]);

  useEffect(() => {
    lastSnapshotKeyRef.current = "";
  }, [carId]);

  useEffect(() => {
    const initialTab = getInitialTab(true, isCashAvailable, isLeasingAvailable, isRentAvailable);
    persistActiveTab(initialTab);
  }, [isCashAvailable, isLeasingAvailable, isRentAvailable, persistActiveTab]);

  const handleRentRadioChange = useCallback(
    (event: CalculatorRadioChangeEvent) => {
      if (!calcOptionsRaw) return;
      const newRaw = applyCalculatorRadioChange(JSON.parse(JSON.stringify(calcOptionsRaw)), event);
      setCalcOptionsRaw({ ...newRaw, AuctionId: calcOptionsRaw.AuctionId });
      setRentCalculatorData(mapRentPeriodsToFormOptions(newRaw.RentPeriods));
    },
    [calcOptionsRaw]
  );

  const handleLeasingRadioChange = useCallback(
    (event: CalculatorRadioChangeEvent) => {
      if (!calcOptionsRaw) return;
      const newRaw = applyCalculatorRadioChange(JSON.parse(JSON.stringify(calcOptionsRaw)), event);
      setCalcOptionsRaw({ ...newRaw, AuctionId: calcOptionsRaw.AuctionId });
      setLeasingCalculatorData(mapLeasingPeriodsToFormOptions(newRaw.LeasingPeriods));
    },
    [calcOptionsRaw]
  );

  useEffect(() => {
    if (!calcOptionsRaw || !rentCalculatorData || !leasingCalculatorData) {
      return;
    }

    const calcOptions = buildCalcOptionsForContactUrl(rentCalculatorData, leasingCalculatorData);
    const isAfterLease = pageType === "afterLease";
    const url = appendBrokerUrlParams(
      createURLToContactSection(
        calcOptions,
        { AuctionId: calcOptionsRaw.AuctionId, LeasingPeriods: calcOptionsRaw.LeasingPeriods },
        activePackageState,
        activeTab,
        isAfterLease
      ),
      pageType,
      vendorType,
      login
    );

    setUrlObject((prev) => (prev === url ? prev : url));
    onContactUrlRef.current?.(url);
  }, [rentCalculatorData, leasingCalculatorData, activeTab, calcOptionsRaw, activePackageState, pageType, vendorType, login]);

  useEffect(() => {
    if (!calcOptionsRaw || !rentCalculatorData || !leasingCalculatorData) return;

    const prices = getSelectedPricesFromCalculatorData(calcOptionsRaw, activeTab);
    const baseInstallmentPrice = prices?.installmentPrice;
    if (baseInstallmentPrice == null) {
      return;
    }

    const withPackages = installmentWithPackages ?? baseInstallmentPrice;
    setInstallmentPrice((prev) => (prev === withPackages ? prev : withPackages));
    setInstallmentPriceWithoutPackages((prev) =>
      prev === baseInstallmentPrice ? prev : baseInstallmentPrice
    );

    const calcOptions = buildCalcOptionsForContactUrl(rentCalculatorData, leasingCalculatorData);
    const contactFormPayload =
      buildPrintOfferPayload(
        calcOptions,
        calcOptionsRaw.AuctionId,
        activePackageState,
        prices,
        activeTab,
        pageType === "afterLease",
        withPackages,
        totalPriceBrutto
      )?.CalculatorContactFormData ?? null;

    const snapshotKey = [
      activeTab,
      withPackages,
      baseInstallmentPrice,
      urlObject,
      Object.values(activePackageState)
        .filter((entry) => entry.isChecked)
        .map((entry) => entry.Value)
        .sort()
        .join(",")
    ].join("|");

    if (snapshotKey === lastSnapshotKeyRef.current) {
      return;
    }

    lastSnapshotKeyRef.current = snapshotKey;

    onCalculatorSnapshotRef.current?.({
      calculatorData: calcOptionsRaw,
      selectedPrices: prices,
      activePackageState,
      activeTab,
      installmentPrice: withPackages,
      installmentPriceWithoutPackages: baseInstallmentPrice,
      urlObject,
      contactFormPayload
    });
  }, [
    calcOptionsRaw,
    rentCalculatorData,
    leasingCalculatorData,
    activeTab,
    installmentWithPackages,
    activePackageState,
    urlObject,
    pageType,
    totalPriceBrutto
  ]);

  const infoPackageItems = useMemo(() => {
    const additions =
      pageType === "new" && activeTab === 1
        ? calculatorDataForPackages.RentAdditions
        : activeTab === 1
          ? calculatorDataForPackages.RentAdditions
          : calculatorDataForPackages.LeasingAdditions;

    const items = buildAdditionalServiceItemsFromAdditions(
      additions,
      literals,
      isElectric,
      activeTab,
      pageType,
      pageType === "afterLease" && activeTab === 1
        ? calculatorDataForPackages.LeasingAdditions
        : undefined
    );
    return filterInfoPackageItems(items, pageType, activeTab);
  }, [calculatorDataForPackages, pageType, activeTab, literals, isElectric]);

  const selectedPrices = calcOptionsRaw ? getSelectedPricesFromCalculatorData(calcOptionsRaw, activeTab) : null;

  const printOfferPayload = useMemo((): PrintOfferPayload | null => {
    if (!calcOptionsRaw || !rentCalculatorData || !leasingCalculatorData) {
      return null;
    }

    const calcOptions = buildCalcOptionsForContactUrl(rentCalculatorData, leasingCalculatorData);
    const isAfterLease = pageType === "afterLease";

    const payload = buildPrintOfferPayload(
      calcOptions,
      calcOptionsRaw.AuctionId,
      activePackageState,
      selectedPrices,
      activeTab,
      isAfterLease,
      installmentPrice,
      totalPriceBrutto
    );

    if (!payload || !activePromo || !isPromoTab(activePromo, activeTab)) {
      return payload;
    }
    return {
      ...payload,
      PromoCode: activePromo.code,
      ...(activePromo.referer ? { ReffererId: activePromo.referer } : {})
    };
  }, [
    calcOptionsRaw,
    rentCalculatorData,
    leasingCalculatorData,
    activePackageState,
    selectedPrices,
    activeTab,
    pageType,
    installmentPrice,
    totalPriceBrutto,
    activePromo
  ]);

  return {
    isLoading,
    isError,
    showCalc,
    setShowCalc,
    activeTab,
    persistActiveTab,
    calcOptionsRaw,
    rentCalculatorData,
    leasingCalculatorData,
    handleRentRadioChange,
    handleLeasingRadioChange,
    activePackageState,
    packageTiles,
    installmentPrice,
    installmentPriceWithoutPackages,
    selectedPrices,
    infoPackageItems,
    urlObject,
    printOfferPayload,
    carState: pageType === "new" ? "new" : "afterLease",
    vendorType,
    promoCodeInput,
    setPromoCodeInput,
    applyPromoCode,
    promoCodeMessage:
      promoCodeState.status === "error"
        ? promoCodeState.message
        : promoCodeState.status === "success" && isPromoCodeForInput
          ? isPromoCodeForActiveTab
            ? promoCodeState.message
            : PROMO_CODE_MESSAGES.notApplicableForTab
          : "",
    promoCodeStatus: (isPromoCodeApplied ? "success" : promoCodeState.status === "error" ? "error" : null) as
      | "success"
      | "error"
      | null,
    isPromoCodeApplied,
    isPromoCodeLoading: promoCodeState.status === "loading"
  };
}
